import { useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import { formatTime } from '@brimo/shared';
import { clsx } from '../ds/clsx';
import { Banner, FilterChip, Sheet, Skeleton, TextLink } from '../ds/components';
import { useEscapeKey, useFocusTrap, useIsMobile, useOutsideClick } from '../ds/hooks';
import { errorMessage } from '../lib/api';
import {
  groupByDay,
  useMarkRead,
  useNotifications,
  type NotificationFilter,
  type NotificationItem,
} from './notifications';
import styles from './NotificationsPanel.module.css';

export interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  /** The bell; clicks on it do not count as outside clicks and focus returns to it. */
  anchorRef: RefObject<HTMLElement | null>;
}

const filters: ReadonlyArray<{ value: NotificationFilter; label: string }> = [
  { value: 'all', label: 'Alle' },
  { value: 'unread', label: 'Ungelesen' },
  { value: 'mentions', label: 'Erwähnungen' },
];

const dotClass: Record<NotificationItem['type'], string | undefined> = {
  success: styles.dotSuccess,
  task: styles.dotTask,
  mention: styles.dotMention,
  notice: styles.dotNotice,
};

function NotificationRow({
  item,
  onOpen,
}: {
  item: NotificationItem;
  onOpen: (item: NotificationItem) => void;
}) {
  const unread = item.readAt === null;
  return (
    <button
      type="button"
      className={clsx(styles.row, unread && styles.rowUnread)}
      onClick={() => onOpen(item)}
      aria-label={unread ? `Ungelesen: ${item.title}` : undefined}
    >
      <span className={clsx(styles.dot, unread ? dotClass[item.type] : styles.dotRead)} aria-hidden="true" />
      <span className={styles.rowText}>
        <span className={clsx(styles.rowTitle, unread && styles.rowTitleUnread)}>{item.title}</span>
        {item.text ? <span className={styles.rowSub}>{item.text}</span> : null}
      </span>
      <span className={styles.rowTime}>{formatTime(item.createdAt)}</span>
    </button>
  );
}

function PanelContent({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const query = useNotifications(filter, open);
  const markRead = useMarkRead();
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const unread = query.data?.pages[0]?.unread ?? 0;
  const groups = groupByDay(items);

  function openItem(item: NotificationItem) {
    if (item.readAt === null) markRead.mutate({ ids: [item.id] });
    if (item.href) {
      onClose();
      void navigate(item.href);
    }
  }

  return (
    <>
      <div className={styles.head}>
        <span className={styles.title}>Benachrichtigungen</span>
        {unread > 0 ? (
          <span className={styles.headAction}>
            <TextLink
              as="button"
              size="small"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate({ all: true })}
            >
              Alle als gelesen markieren
            </TextLink>
          </span>
        ) : null}
      </div>
      <div className={styles.filters} role="group" aria-label="Filter">
        {filters.map((entry) => (
          <FilterChip
            key={entry.value}
            label={entry.label}
            active={filter === entry.value}
            className={styles.chip}
            onClick={() => setFilter(entry.value)}
          />
        ))}
      </div>
      <div className={styles.list}>
        {query.isError ? (
          <div className={styles.inlineBanner}>
            <Banner tone="danger">{errorMessage(query.error)}</Banner>
          </div>
        ) : query.isPending ? (
          <div className={styles.skeleton} aria-busy="true">
            <Skeleton width="60%" />
            <Skeleton width="80%" />
            <Skeleton width="50%" />
          </div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{filter === 'all' ? 'Alles gelesen.' : 'Keine Einträge.'}</div>
            <div className={styles.emptyText}>
              Neue Ereignisse zu Ihren Kontakten, Deals und Tasks erscheinen hier.
            </div>
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.key} className={styles.day}>
              <div className={styles.dayLabel}>{group.label}</div>
              {group.items.map((item) => (
                <NotificationRow key={item.id} item={item} onOpen={openItem} />
              ))}
            </div>
          ))
        )}
        {markRead.isError ? (
          <div className={styles.inlineBanner}>
            <Banner tone="danger">{errorMessage(markRead.error)}</Banner>
          </div>
        ) : null}
        {query.hasNextPage ? (
          <div className={styles.more}>
            <TextLink
              as="button"
              size="small"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Weitere laden
            </TextLink>
          </div>
        ) : null}
      </div>
      <div className={styles.foot}>
        <span>Ältere Einträge nach 30 Tagen im Verlauf</span>
        <span className={styles.footAction}>
          <TextLink
            as="button"
            size="small"
            onClick={() => {
              onClose();
              void navigate('/settings/notifications');
            }}
          >
            Einstellungen
          </TextLink>
        </span>
      </div>
    </>
  );
}

/** The bell panel (Shell 07/08): 420 px under the bell on desktop, a bottom sheet on mobile. */
export function NotificationsPanel({ open, onClose, anchorRef }: NotificationsPanelProps) {
  const mobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const desktop = open && !mobile;
  useEscapeKey(onClose, desktop);
  useOutsideClick([panelRef, anchorRef], onClose, desktop);
  useFocusTrap(panelRef, desktop, { initialFocus: 'first' });

  if (!open) return null;

  if (mobile) {
    return (
      <Sheet open={open} onClose={onClose} label="Benachrichtigungen" height="full">
        <div className={styles.sheetContent}>
          <PanelContent open={open} onClose={onClose} />
        </div>
      </Sheet>
    );
  }

  return (
    <div ref={panelRef} role="dialog" aria-label="Benachrichtigungen" tabIndex={-1} className={styles.panel}>
      <PanelContent open={open} onClose={onClose} />
    </div>
  );
}
